/**
 * Company Providers Tab Component
 * Displays and manages company-provider (clinic) links as mini-cards
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  IconButton,
  Chip,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventBusy as EndIcon,
  LocalHospital as ClinicIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { CompanyProviderLink } from '../../../../types/company';
import { companyService } from '../../../../services/companyService';
import { providerService } from '../../../../services/providerService';
import CompanyProviderFormModal from '../modals/CompanyProviderFormModal';
import { ProfileSection } from '../../../../components/common';

interface CompanyProvidersTabProps {
  companyId: string;
}

const CompanyProvidersTab: React.FC<CompanyProvidersTabProps> = ({
  companyId,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [links, setLinks] = useState<CompanyProviderLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<CompanyProviderLink | undefined>();
  const [modalError, setModalError] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<CompanyProviderLink | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // End dialog state
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [linkToEnd, setLinkToEnd] = useState<CompanyProviderLink | null>(null);
  const [endLoading, setEndLoading] = useState(false);

  // Provider list for dropdown
  const [providers, setProviders] = useState<Array<{ id: number; name: string }>>([]);

  const loadLinks = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyService.getLinkedProviders(companyId, { activeOnly });
      setLinks(data);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao carregar clínicas vinculadas');
      enqueueSnackbar('Erro ao carregar clínicas vinculadas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = async () => {
    try {
      const result = await providerService.getProvidersForDropdown();
      setProviders(result);
    } catch (err) {
      console.error('Error loading providers:', err);
    }
  };

  useEffect(() => {
    loadLinks();
    loadProviders();
  }, [companyId, activeOnly]);

  const handleAdd = () => {
    setSelectedLink(undefined);
    setModalError(null);
    setModalOpen(true);
  };

  const handleEdit = (link: CompanyProviderLink) => {
    setSelectedLink(link);
    setModalError(null);
    setModalOpen(true);
  };

  const handleDeleteClick = (link: CompanyProviderLink) => {
    setLinkToDelete(link);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!linkToDelete) return;

    setDeleteLoading(true);
    try {
      await companyService.removeProviderLink(companyId, linkToDelete.id.toString());
      enqueueSnackbar('Vínculo excluído com sucesso', { variant: 'success' });
      loadLinks();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao excluir vínculo', { variant: 'error' });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setLinkToDelete(null);
    }
  };

  const handleEndClick = (link: CompanyProviderLink) => {
    setLinkToEnd(link);
    setEndDialogOpen(true);
  };

  const handleEndConfirm = async () => {
    if (!linkToEnd) return;

    setEndLoading(true);
    try {
      await companyService.endProviderLink(companyId, linkToEnd.id.toString());
      enqueueSnackbar('Vínculo encerrado com sucesso', { variant: 'success' });
      loadLinks();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao encerrar vínculo', { variant: 'error' });
    } finally {
      setEndLoading(false);
      setEndDialogOpen(false);
      setLinkToEnd(null);
    }
  };

  const handleFormSubmit = async (data: { providerId: string; startDate: string; endDate: string; notes: string }) => {
    setModalError(null);
    try {
      if (selectedLink) {
        await companyService.updateProviderLink(
          companyId,
          selectedLink.id.toString(),
          {
            startDate: data.startDate,
            endDate: data.endDate || null,
            notes: data.notes || undefined,
          }
        );
        enqueueSnackbar('Vínculo atualizado com sucesso', { variant: 'success' });
      } else {
        await companyService.linkProvider(companyId, {
          providerId: parseInt(data.providerId),
          startDate: data.startDate,
          endDate: data.endDate || undefined,
          notes: data.notes || undefined,
        });
        enqueueSnackbar('Prestador vinculado com sucesso', { variant: 'success' });
      }
      loadLinks();
    } catch (err: any) {
      setModalError(err.message ?? 'Erro ao salvar vínculo');
      throw err;
    }
  };

  const isActive = (link: CompanyProviderLink) => !link.endDate;

  return (
    <ProfileSection
      title="Clínicas Vinculadas"
      icon={<ClinicIcon />}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Apenas ativos</Typography>}
          />
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
            Vincular
          </Button>
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : links.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" gutterBottom>
            Nenhuma clínica vinculada
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
            Vincular Prestador
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {links.map((link) => {
            const name = link.provider?.tradeName || link.provider?.legalName || '-';

            return (
              <Grid key={link.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Chip
                    label="Clínica"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  <Typography variant="subtitle2" sx={{ mt: 1 }} noWrap>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Desde {new Date(link.startDate).toLocaleDateString('pt-BR')}
                    {link.endDate &&
                      ` até ${new Date(link.endDate).toLocaleDateString('pt-BR')}`}
                  </Typography>
                  {link.notes && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {link.notes}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 'auto',
                      pt: 1,
                    }}
                  >
                    <Chip
                      label={isActive(link) ? 'Ativo' : 'Encerrado'}
                      size="small"
                      color={isActive(link) ? 'success' : 'default'}
                    />
                    <Box>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(link)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isActive(link) && (
                        <Tooltip title="Encerrar">
                          <IconButton size="small" onClick={() => handleEndClick(link)}>
                            <EndIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(link)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Form Modal */}
      <CompanyProviderFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleFormSubmit}
        link={selectedLink}
        providers={selectedLink ? providers : providers.filter(
          (p) => !links.some((l) => Number(l.providerId) === p.id && !l.endDate)
        )}
        error={modalError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este vínculo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Link Confirmation Dialog */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)}>
        <DialogTitle>Encerrar Vínculo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja encerrar este vínculo? A data de fim será
            definida como hoje.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialogOpen(false)} disabled={endLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleEndConfirm}
            color="warning"
            variant="contained"
            disabled={endLoading}
          >
            {endLoading ? 'Encerrando...' : 'Encerrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfileSection>
  );
};

export default CompanyProvidersTab;
